define((require) => {
    "use strict";

    const $              = require("jquery");
    const EmbedMixin     = require("shared/ui/experience/widgets/embedWidget/EmbedMixin");
    const ViewerLightbox = require("shared/ui/experience/action/lightbox/ViewerLightbox");
    const ViewerEvents   = require("shared/services/ViewerEvents");
    const UrlUtil        = require("shared/util/UrlUtil");
    const Window         = require("shared/services/Window");
    const channelBridge  = require("shared/ui/viewer/ChannelBridge");
    const Action         = require("shared/ui/experience/action/Action");

    const TYPE = "lightbox";

    /**
     * Closes the lightbox from within the lightbox (code) itself. <p>
     *
     * See http://documentation.zmags.com/lightboxapi/symbols/com.zmags.api.Lightbox.html
     *
     * @param {Window}         win    The iframe window object, not the global one; never false. This means the
     *                                export will never conflict with other Experiences/Channels.
     * @param {LightboxAction} action The Lightbox action to close; never false.
     */
    function exportClose(win, action) {
        win.com = win.com || {};
        win.com.zmags = win.com.zmags || {};
        win.com.zmags.api = win.com.zmags.api || {};
        win.com.zmags.api.Lightbox = {
            close() { action.close(); }
        };
    }

    /**
     * @class LightboxAction
     * Action for opening an URL in an iframe in a lightbox. <p>
     *
     * This has partly support for embedded Experiences and Groups, akin to the Embed Widget. However, an embedded
     * Experience opening up another lightbox will by design close the first one.
     *
     * @author Bo Gotthardt
     * @author Gunni Rode
     *
     * @constructor
     */
    return Action.extend("LightboxAction", {

        defaults: {
            url:    "",
            height: 400,
            width:  600
        },

        // Mixin common embed functionality also used by the Embed Widget...
        mixin: EmbedMixin,

        // Triggered on open, LIGHTBOX_CLOSE triggered manually on close, with arguments (this, experienceId, url)...
        _event: ViewerEvents.LIGHTBOX_OPEN,

        // Triggered on close by this implementation, which the Viewer will listen for to ensure the lightbox
        // is closed when still pending on Viewer close...
        _pendingEvent: ViewerEvents.LIGHTBOX_CLOSE,

        // On open, stall, and thus pause, all Experiences reachable from the top most parent Experience, including
        // siblings, i.e. when embedded Experiences are in play, to ensure that all are paused and all pointer events
        // on them are ignored while this lightbox is open.
        _stalled: true, // implies "paused" = true recursively

        /**
         * @private @type {ViewerLightbox}
         * The opened Lightbox.
         */
        _box: null,
        /**
         * @private @type {HTMLElement}
         * The root of the lightbox dom.
         */
        _element: null,
        /**
         * @private
         * The url descriptor for the opened url shown in the Lightbox. <p>
         *
         * This descriptor is passed along "open" and "close" events as the last argument.
         *
         * @see #_box
         */
        _descriptor: null,
        /**
         * @private @type {Boolean}
         * True if we failed to embed the Experience or Group via channels, false otherwise. If so, a direct url
         * will be used as fallback. If the url is not an embedding url, always false.
         */
        _failedToEmbed: false,

        /**
         * @protected
         * Sub-type hook to return the full url to open via this link option, defaulting to just returning the
         * "url" attribute. Based on the returned url, an url descriptor will be created to check for
         * embedded Experiences or Groups.
         *
         * @param  {ExperienceViewModel} experienceViewModel The current Experience View Model; never falsey.
         * @return {String} The url for the lightbox; can be falsey (which means this action cannot be activated).
         */
        _url() {
            return this.get("url");
        },

        _activateable() {
            const url = this._url();
            if (!url) {
                return "Missing url";
            }
            return UrlUtil.getUrlDescriptor(url).valid ? null : "Url invalid";
        },

        /**
         * @protected
         * Activates this action.
         */
        _activate(experienceViewModel) {
            const win = Window.topWindow();
            const scene = experienceViewModel.get("currentScene");

            let embed = false;
            let bridge;
            let channel;
            let iframe; // when no channel bridge is available for Experience/Group or if failed to embed via channels

            // If recursively called because channel embedding failed, re-use existing Lightbox since we will already
            // have issue "event" and listening for "pendingEvent"...
            if (!this._failedToEmbed) {
                // Get url descriptor for embedding support, and to pass it along the events...
                this._descriptor = UrlUtil.getUrlDescriptor(this._url(experienceViewModel));

                // Lightbox, including overlay, *always* lives in the top level dom...
                bridge = channelBridge();
                embed = bridge && (this._descriptor.type !== "url");

                // Wrap the iframe in an element with overflow:hidden to avoid an iOS 6 problem where the iframe
                // is always the size of its content. We create the element in the top level dom, as the quickview is
                // a "singleton" that goes on top of all other content...
                this._element = $(win.document.createElement("div"))
                    .css({
                        overflow: "hidden",
                        width:    this.get("width"),
                        height:   this.get("height")
                    });

                // We have to always use the top most reachable window for the Lightbox, or the overlay and Lightbox
                // itself will be relative to the (embedded) Viewer only. We use the default computed zIndex for
                // the lightbox as we are using the top level dom, i.e. each embedded Experience via a Lightbox will
                // use a higher z index...
                this._box = new ViewerLightbox(this._element, {win});
            }

            // If we have a channel bridge, we still cannot embed until the target element is in the dom, i.e. after
            // the lightbox has opened. This is why we do the embed once the lightbox triggers an "open" event. We
            // always use the top window/document here, since the lightbox uses the top level dom always...
            if (embed) {
                this._descriptor.sameOrigin = true; // for consistency
                this._box.once("open", ()  => {
                    channel = bridge.embed({
                        // These two for channel naming...
                        scene,
                        descriptor: this._descriptor,
                        element: this._element,
                        embedType: TYPE,
                        // Always top level window/dom...
                        channel: {win},
                        // If the embedding via channels somehow fails, default to a direct url. This is deferred, so
                        // the "event" will already have been fired and this Action primed to
                        callback: (populated) => {
                            if (populated) {
                                this._childOptions(experienceViewModel, win, channel.container()); // container = iframe
                            } else {
                                // Manually remove now; the removal on close below will just be ignored when invoked...
                                bridge.removeEmbed(channel, this._failedToEmbed = true); // true: destroy
                                channel = null;
                                // Call this recursively, not "activate", so we do not issue events because of this...
                                this._activate(experienceViewModel);
                            }
                        }
                    });
                });
                this._box.once("close", () => { // also called on forceful close if the channel embedding fails
                    // Do a full destroy as this lives in the global dom, including calling ViewerProxy.close to close
                    // Viewers recursively...
                    bridge.removeEmbed(channel, true);
                });

            // Use an iframe if no bridge or if just any old url not pointing to an embedded Experience/Group. It will
            // not help us much listening for embedded iframe window "unload" events...
            } else {
                iframe = this._createIframe(this._descriptor, {frameBorder: 0})
                    .css({
                        height: this.get("height"),
                        width:  this.get("width")
                    });

                // Manually append the iframe to show in the lightbox. Even we are defaulting to a direct url because
                // the embedding via channels failed, "_element" will be empty as the channel will be been restored...
                this._element.append(iframe);

                // Note that on same domains, 404 will often generate a "load" event because web servers tend
                // to deliver content along side a 404 status code - but the iframe cannot access the state code,
                // only the content...
                iframe.one("load", () => {
                    const iframeWin = iframe.get(0).contentWindow;

                    // If the iframe is on the same domain so we can access it directly, then inject something that
                    // matches the signature of the old lightbox API...
                    if (this._descriptor.sameOrigin) {
                        exportClose(iframeWin, this);

                        // If the iframe is yanked out, remember to close the lightbox as well. We can only do this
                        // for same origins or we will get a security error...
                        // sometimes this seems to be called numerous times, so this ensures it will not!
                        Window.unload(() => true, iframeWin);

                        // Same origin, so safe to sniff the document title for potential error code; this will not
                        // break Lightbox functionality, we just want to report it so we can track it in LogStash...
                        this._statusCode(iframeWin);
                    }

                    // If we get a command from the old lightbox API, handle it with our lightbox. The lightbox
                    // API always post to its parent, i.e. the parent of the iframe, so we have to listen there...
                    const listener = Window.until("message", (e) => {
                        if (e.originalEvent.data === "closeLightbox") { // See http://documentation.zmags.com/lightboxapi/symbols/com.zmags.api.Lightbox.html
                            this.close();
                            return true; // done, so remove!
                        }
                    }, Window.parent());

                    this._box.once("close", listener.off); // already scoped
                });

                if (this._descriptor.type !== "url" && this._descriptor.sameOrigin) {
                    this._childOptions(experienceViewModel, win, iframe[0]);
                }

                // TODO: consider logic for blanking urls like for EmbedWidget, e.g. to stop videos.
                iframe.prop("src", UrlUtil.getEmbedURL(this._descriptor, TYPE, scene)); // e.g. "?lightbox=.." in url
            }

            // Return raw embed url as an event argument, ignoring query parameters, and even when in Channel mode,
            // making it possible to track the same Experience regardless of how it was used based on the same url...
            const eventArgs = {url: UrlUtil.getEmbedURL(this._descriptor), context: TYPE}; // "raw" url!

            // Attached close handler to trigger pending event and open, but only so it if not already done!...
            if (!this._failedToEmbed) {
                // Trigger additional event on close for this lightbox action. The Viewer - and others - will be listening
                // for this event, keeping a reference to this Action to close. This handler will also reset this action
                // for re-activation, e.g. if the widget using this action is clicked again...
                this._box.once("close", () => {
                    this._trigger("pendingEvent", experienceViewModel, eventArgs); // Viewer event thus like LIGHTBOX_CLOSE: (this, id, eventArgs)

                    // Remember to close embedded Viewer recursively for direct urls, if any, as we do not have
                    // an in-place Channel to call the Viewer proxy for us...
                    if (iframe && this._descriptor.type !== "url") {
                        Window.viewer(iframe[0], "state", {name: "closed", value: true}); // will recurse
                    }

                    this._box = null;
                    this._element.empty(); // make sure iframe is explicitly garbage collected...
                    this._element = null;
                    this._descriptor = null;
                    this._failedToEmbed = false;
                });

                this._box.open();
            }
            return Object.assign({actionId: this.id, widgetId: this.attributes.widgetId}, eventArgs); // Viewer event thus like LIGHTBOX_OPEN: (this, id, eventArgs)
        },

        /**
         * @private
         * Make sure the Viewer loaded in the Lightbox iframe knows that this Viewer is the parent, not the top most
         * one in the window used for the Lightbox overlay and Lightbox itself.
         *
         * @param {ExperienceViewModel} experienceViewModel The Experience View Model; never null.
         * @param {Window}              win                 The top most window the Lightbox lives in; never null.
         * @param {HTMLElement}         iframe              The iframe storing the embedded Viewer; never null.
         */
        _childOptions(experienceViewModel, win, iframe) {
            // For direct urls...
            Window.viewer(win, "register", iframe, null, Window.viewer());

            // Since this is an Action, and not a Widget, it is not directly updated based on recursive Viewer
            // state updates like "change:active", triggered via re-render that for example pass on the active
            // state to the EmbedWidgetView. We thus have to listen for "change:active" changes and relay them to
            // the embedded Experience here...
            this.listenTo(experienceViewModel, "change:active", (model, active) => {
                Window.viewer(iframe, "state", {name: "active", value: active}); // will recurse!
            });
        },

        /**
         * Manually closes the opened Lightbox, if still open. <p>
         *
         * The Viewer will ensure this is called if the Viewer is closed while the Lightbox is open.
         */
        close() {
            if (this._box) {
                this._box.close();
            }
        }

    }, {
        TYPE
    });
});



// WEBPACK FOOTER //
// ./src/shared/ui/experience/action/lightbox/LightboxAction.js